import React from "react";
import SEO from "@/lib/seo-static.json";
import { ArrowRight } from "lucide-react";
import { VALUES, STATS, SITE } from "@/lib/site";
import { Container, Section, SectionHeader, Reveal, Kicker, CTAButton, usePageTitle } from "@/components/ui-kit";
import { StatsBand } from "@/components/sections/Bands";
import { Testimonials, CTABand } from "@/components/sections/Social";

const TIMELINE = [
  { y: "The itch", t: "Three tools, one WhatsApp group", d: "Fixlr started inside a property operations team stitching a job tracker, an accounts package and group chats together — and losing money in the gaps." },
  { y: "The engine", t: "Multi-tenant from day one", d: "Rather than a single-company tool, we built a tenant-isolated engine so FM companies, property managers and MSP partners could run on the same core — and white-label it." },
  { y: "The lane", t: "Suppliers as first-class users", d: "Most software treats contractors as an afterthought. We gave them a real portal, compliance tracking and an AI that ranks them fairly." },
  { y: "Now", t: "Decision co-pilots, not chatbots", d: "AI Supplier Matching, emergency ladders and self-scheduling contracts: the platform makes the decisions you'd rather not spend your day on, and shows its reasoning." },
];

export default function About() {
  usePageTitle(SEO["/about"].title, SEO["/about"].description, SEO["/about"].image);
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-60" aria-hidden />
        <div className="orb h-[460px] w-[460px] -top-40 right-[-100px] bg-mk-blue/25" aria-hidden />
        <Container className="relative pt-14 pb-10 lg:pt-20">
          <Kicker>About Fixlr</Kicker>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-mk-text leading-[1] text-balance max-w-4xl">We build the engine that lets maintenance teams <span className="text-mk-lime">fix more</span> and <span className="text-mk-blue">manage less</span>.</h1>
          <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed max-w-2xl">Fixlr is a multi-tenant, white-label operations platform for property, facilities and field-service teams — and for the partners who resell it as their own. {SITE.description}</p>
        </Container>
      </section>
      <StatsBand />

      <Section className="bg-mk-surface/40 border-y border-mk-border">
        <Container>
          <SectionHeader eyebrow="What we believe" title="Built for operators. Multi-tenant by default. AI that decides." />
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {VALUES.map((v, i) => <Reveal key={v.title} delay={i * 0.08} className="rounded-2xl border border-mk-border bg-mk-bg p-7"><span className="grid place-items-center h-11 w-11 rounded-xl bg-mk-lime/10 text-mk-lime"><v.icon className="h-5 w-5" /></span><h3 className="mt-5 font-display text-xl font-semibold text-mk-text">{v.title}</h3><p className="mt-2 text-sm text-mk-dim leading-relaxed">{v.desc}</p></Reveal>)}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeader eyebrow="The story" title="From one ops team's spreadsheet to a platform other companies resell." />
          <div className="mt-12 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-mk-border" aria-hidden />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.t} delay={i * 0.06} className={`relative md:grid md:grid-cols-2 md:gap-12 ${i % 2 ? "" : ""}`}>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "md:col-start-2 md:pl-12" : "md:text-right md:pr-12"}`}>
                    <span className="font-mono text-xs text-mk-lime">{t.y}</span>
                    <h3 className="mt-2 font-display text-xl font-semibold text-mk-text">{t.t}</h3>
                    <p className="mt-2 text-sm text-mk-dim leading-relaxed">{t.d}</p>
                  </div>
                  <span className="absolute left-4 md:left-1/2 top-1.5 -translate-x-1/2 h-3 w-3 rounded-full bg-mk-lime ring-4 ring-mk-bg" />
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      <Section className="bg-mk-surface/40 border-y border-mk-border">
        <Container className="grid lg:grid-cols-2 gap-10 items-center">
          <SectionHeader eyebrow="Partners" title="Resell Fixlr as your own product." desc="MSPs, FM consultancies and software partners run Fixlr under their own domain and brand, with a platform-admin layer for their downstream customers. We handle the engine; you own the relationship." />
          <Reveal className="rounded-3xl border border-mk-blue/40 bg-mk-blue/[0.06] p-8 glow-blue">
            <div className="grid grid-cols-2 gap-4">{[["Custom domain", "auto TLS"], ["Per-tenant brand", "logo · colours · sender"], ["Platform dashboard", "MRR · churn · tenants"], ["Isolation", "at every DB query"]].map(([a, b]) => <div key={a} className="rounded-xl border border-mk-border bg-mk-bg p-4"><div className="font-semibold text-mk-text text-sm">{a}</div><div className="font-mono text-[11px] text-mk-dim mt-1">{b}</div></div>)}</div>
            <div className="mt-6 flex flex-wrap gap-3"><CTAButton to="/solutions#partners" variant="blue" testId="about-partners-btn">Partner programme <ArrowRight className="h-4 w-4" /></CTAButton><CTAButton to="/contact" variant="ghost" testId="about-contact-btn">Talk to us</CTAButton></div>
          </Reveal>
        </Container>
      </Section>

      <Testimonials />
      <CTABand />
    </div>
  );
}
